import type { RootState } from 'app/store/rootReducer';
import { signIn, getCurrentUser } from './authSlice';
import { User } from 'API';
// SEE-1232 rows k/l: getUser / updateUser read and write CDK AppSync (userService). The
// superseded documents stay in ./userProfileQueries, unreferenced (SEE-1238 removes them).
import { fileAccessService } from 'app/services/fileAccess.service';
import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import serializeError, { ErrorLike } from 'util/serializeError';
import type { UpdateUserInput } from 'API';
// SEE-191: first-run banner writes go to the CDK AppSync API (user.service).
import type { CompleteBannerInput } from 'CDK_API';
import { userService } from 'app/services/user.service';

export type Language = 'en' | 'es';

export interface UserProfileState {
  isUserProfileStateUnknown: boolean;
  profile?: User;
  picture?: string;
  language: Language;
}

const initialState: UserProfileState = {
  isUserProfileStateUnknown: true,
  language: (localStorage.getItem('i18nextLng') as Language) || 'en',
};

export const getUserProfile = createAsyncThunk<User | null | undefined, string, { rejectValue: ErrorLike }>(
  'userProfile/getUserProfile',
  async (userId: string, { rejectWithValue, dispatch }) => {
    // Transport flip only — same `{ id: userSub }` argument, same selection, same result.
    return userService
      .getUser<User>(userId)
      .then((profile) => {
        if (profile) {
          if (profile.picture) {
            dispatch(getUserPicture(profile.picture));
          }

          return profile as User;
        }
      })
      .catch((error) => rejectWithValue(serializeError(error)));
  }
);

export const getUserPicture = createAsyncThunk<string, string, { rejectValue: ErrorLike }>(
  'userProfile/getUserPicture',
  async (picture: string, { rejectWithValue }) => {
    return fileAccessService.getDownloadUrl(picture).catch((error) => rejectWithValue(serializeError(error)));
  }
);

interface SetUserPictureArgs {
  id: string;
  /** The user's file name. The stored key is chosen server-side under the caller's own id. */
  fileName: string;
  file: File;
}

export const setUserPicture = createAsyncThunk(
  'userProfile/uploadProfilePicture',
  async ({ id, fileName, file }: SetUserPictureArgs, { dispatch }) => {
    // SEE-1319: the key is derived from the signed-in user, so a caller-supplied
    // name can no longer point at (and overwrite) someone else's picture.
    const uploaded = await fileAccessService.uploadFile({
      kind: 'PROFILE_PICTURE',
      file,
      fileName,
      contentType: file.type,
    });
    // Await the save so a failed updateUser rejects this thunk and reaches the
    // callers' catch (the snackbar), instead of showing the new picture until reload.
    await dispatch(updateUserProfile({ id, picture: uploaded.key })).unwrap();
    dispatch(getUserPicture(uploaded.key));
  }
);

export const updateUserProfile = createAsyncThunk<
  User | null | undefined,
  UpdateUserInput,
  { rejectValue: ErrorLike }
>('userProfile/updateUserProfile', async (args, { rejectWithValue }) => {
  return userService
    .updateUser<User>(args as unknown as Record<string, unknown>)
    .then((profile) => {
      if (profile) {
        return profile as User;
      }
    })
    .catch((error) => rejectWithValue(serializeError(error)));
});

export const updateDescriptionBanner = createAsyncThunk(
  'userProfile/completeFirstRun',
  async (input: CompleteBannerInput) => {
    // SEE-191: served by the CDK first-run-resolver Lambda.
    return userService.completeFirstRunBanner(input);
  }
);

const userProfileSlice = createSlice({
  name: 'userProfile',
  initialState,
  reducers: {
    setLanguage: (state, action) => {
      state.language = action.payload;
    },
    resetUserProfileSlice: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(getUserPicture.fulfilled, (state, action) => {
      if (action.payload) {
        state.picture = action.payload;
      }
    });

    builder.addCase(updateDescriptionBanner.fulfilled, (state, action) => {
      const { data } = action.meta.arg;

      if (state.profile?.firstRunBanner) {
        state.profile.firstRunBanner = {
          ...state.profile.firstRunBanner,
          [data.firstRunValue]: true,
        };
      }
    });

    builder.addMatcher(isAnyOf(getUserProfile.fulfilled, updateUserProfile.fulfilled), (state, action) => {
      if (action.payload) {
        state.profile = action.payload;
        state.isUserProfileStateUnknown = false;
      }
    });

    builder.addMatcher(isAnyOf(getCurrentUser.rejected, signIn.rejected), (state) => {
      state.profile = undefined;
      state.picture = undefined;
      state.isUserProfileStateUnknown = true;
    });
  },
});

export const { setLanguage } = userProfileSlice.actions;

export const { resetUserProfileSlice } = userProfileSlice.actions;

export const selectLanguage = (state: RootState): Language => state.userProfile.language;

export const selectUserProfile = (state: RootState): UserProfileState =>
  state.userProfile as UserProfileState;

export default userProfileSlice.reducer;
